import { localCatalog } from '../data/catalog'
import { openingHours } from '../data/content'
import { customerName } from '../admin/customers/customerRules'
import {
  DEFAULT_STAFF_ID,
  FALLBACK_DURATION,
  availability,
  conflictsFor,
  hasStarted,
  outsideHours,
  reminderAt,
  type TimeSpan,
} from '../core/rules/agenda'
import { byStart, iso, nowStamp, today } from '../core/format/dates'
import { ApiError } from '../core/api/http'
import { courseStatus } from '../utils/account'
import { isKuurExpired, noShowUsesSession } from '../core/rules/kuur'
import { policy } from '../data/policies'
import { db, type AppointmentRow } from './db'
import { demoId, reply } from './reply'
import { demoStaff } from './seed/agenda'
import { salonExceptions, staff } from '../data/staff'
import { canPerform, performerChoice, salonDayFor, staffName } from '../core/rules/salonDays'
import type { AvailabilityFilter } from '../core/types/backend'
import type { Account, Visit } from '../core/types/account'
import type { AgendaAbsenceResult, AgendaAppointment, AgendaNewRequest, AgendaSeriesResult, AppointmentProducts, AvailabilityDay } from '../core/types/agenda'
import { seriesDates, seriesProblem } from '../core/rules/series'
import { absenceException, absencePlan, withException } from '../core/rules/absence'
import { bookingProblem, requestProblem } from '../core/rules/staffPermissions'
import { ownOnly, type Viewer } from '../core/rules/staffAccess'
import { demoAdminIdentity } from './adminIdentity'
import type { AdminBackend } from '../core/types/backend'

/**
 * DEMO — de agenda aan de kant van de backend. Dezelfde afsprakentabel als de webshop,
 * Mijn omgeving en het klantdossier (demo/db.ts): één afspraak bestaat maar één keer.
 * De server doet hetzelfde, maar controleert botsingen met een lock (LARAVEL.md §11).
 */

export const durationOf = (slug: string) => localCatalog.treatments.find((t) => t.slug === slug)?.durationMinutes ?? FALLBACK_DURATION
const kindOf = (slug: string) => (localCatalog.treatments.some((t) => t.slug === slug && t.online) ? 'video' : 'salon')

const stampOf = (d: Date) => `${iso(d)}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`

const contactOf = (row: AppointmentRow, account: Account | null): AgendaAppointment['customer'] | null => {
  if (account) {
    const c = account.customer
    const course = account.course
    return {
      id: c.id,
      name: customerName(c),
      email: c.email,
      phone: c.phone || undefined,
      firstVisit: account.visits.length === 0,
      ...(course
        ? {
            kuur: {
              treatmentSlug: course.treatmentSlug,
              total: course.total,
              prepaid: Boolean(course.prepaid),
              expired: isKuurExpired(course, today()),
              ...(course.staffId ? { staffId: course.staffId } : {}),
            },
          }
        : {}),
    }
  }
  return row.guest ? { name: row.guest.name, email: row.guest.email, phone: row.guest.phone, firstVisit: row.guest.firstVisit } : null
}

/** Een rij zoals de agenda hem ziet. `null` = klant bestaat niet meer (account verwijderd). */
export function toAgenda(row: AppointmentRow): AgendaAppointment | null {
  const account = row.customerId ? db.account(row.customerId) : null
  const customer = contactOf(row, account)
  if (!customer) return null
  const remindAt = reminderAt(row)
  // De server zet dit bij het versturen; de demo doet alsof hij op tijd verstuurd is.
  const sent = row.status === 'bevestigd' && row.outcome !== 'geannuleerd' && remindAt.getTime() <= Date.now() && row.receivedAt <= stampOf(remindAt)
  return {
    id: row.id,
    treatmentSlug: row.treatmentSlug,
    date: row.date,
    time: row.time,
    kind: row.kind,
    status: row.status,
    ...(row.request ? { request: row.request } : {}),
    ...(row.session ? { session: row.session } : {}),
    staffId: row.staffId ?? DEFAULT_STAFF_ID,
    durationMinutes: row.durationMinutes ?? durationOf(row.treatmentSlug),
    customer,
    receivedAt: row.receivedAt,
    ...(row.outcome ? { outcome: row.outcome } : {}),
    ...(row.cancelledBy ? { cancelledBy: row.cancelledBy } : {}),
    ...(row.products ? { products: row.products } : {}),
    ...(row.treatmentNote ? { treatmentNote: row.treatmentNote } : {}),
    ...(sent ? { reminderSentAt: stampOf(remindAt) } : {}),
    ...(row.saleId ? { saleId: row.saleId } : {}),
    ...(row.arrivedAt ? { arrivedAt: row.arrivedAt } : {}),
    ...(row.handledBy ? { handledBy: row.handledBy } : {}),
  }
}

/**
 * Wie er in dit tabblad is ingelogd, als kijker voor de rechtenregels (zoals de server uit de
 * sessie): "alles" met plannen voor de hele salon, anders alleen eigen dagen.
 */
const viewerNow = (): Viewer => demoAdminIdentity.viewer('agenda.plan')
const rules = () => ({ hours: openingHours, exceptions: salonExceptions, staff })

/** 403 als een collega aan een afspraak van een ander komt (de server stuurt die ook niet mee). */
function assertMine(row: AppointmentRow) {
  if (!demoAdminIdentity.can('agenda.view', { staffId: row.staffId ?? DEFAULT_STAFF_ID })) {
    throw new ApiError('Deze afspraak is niet van jou.', 403, undefined, 'not_your_appointment')
  }
}

const allAgenda = () => db.appointments().map(toAgenda).filter((a): a is AgendaAppointment => a !== null)

/** Zoals de server: botsing = 409 `slot_taken`, tenzij Sarah het bewust toch wil. */
export function assertFree(span: TimeSpan, ignoreId?: string, allowOverlap = false) {
  if (allowOverlap) return
  if (conflictsFor(span, allAgenda(), db.blocks(), { ignoreId }).length > 0) {
    throw new ApiError('Dit tijdstip is net bezet geraakt. Kies een andere tijd.', 409, undefined, 'slot_taken')
  }
}

/**
 * Voor alles wat een klant boekt of verzet (webshop, Mijn omgeving): dezelfde regels als
 * `freeTimes()`. Binnen de openingstijden, met de échte duur vóór sluiten klaar, niet in het
 * verleden, en vrij (`assertFree`). Sarah's eigen planning in agenda en dossier valt hier
 * bewust niet onder: zij mag buiten de tijden plannen.
 */
export function assertBookable(booking: Omit<TimeSpan, 'staffId'> & { treatmentSlug: string; preferredStaffId?: string }, ignoreId?: string): string {
  const { treatmentSlug, preferredStaffId, ...rest } = booking
  // Koos ze iemand ("Bij wie wil je komen?"), dan moet díe er die dag zijn.
  if (preferredStaffId && performerFor(rest.date, treatmentSlug) !== preferredStaffId) {
    throw new ApiError(`${staffName(staff, preferredStaffId)} werkt niet op die dag. Kies een andere dag.`, 422, { date: 'Kies een andere dag.' }, 'performer_unavailable')
  }
  // Wie er die dag werkt doet de afspraak (de klant kiest de dag, de dag bepaalt de persoon).
  const performer = performerFor(rest.date, treatmentSlug)
  const span: TimeSpan = { ...rest, staffId: performer ?? DEFAULT_STAFF_ID }
  if (outsideHours(openingHours, span, salonExceptions) || !performer) {
    throw new ApiError('Deze tijd valt buiten de openingstijden, of de behandeling loopt uit na sluiten. Kies een andere tijd.', 422, { time: 'Kies een andere tijd.' }, 'outside_hours')
  }
  if (hasStarted(span)) throw new ApiError('Deze tijd is al voorbij. Kies een andere tijd.', 422, { time: 'Kies een andere tijd.' }, 'outside_hours')
  assertFree(span, ignoreId)
  return span.staffId
}

/** Wie een online boeking op `date` doet, en of die de behandeling doet. `null` = niet boekbaar. */
export function performerFor(date: string, treatmentSlug: string): string | null {
  const day = salonDayFor(openingHours, salonExceptions, date)
  if (!day || !canPerform(staff, day.staffId, treatmentSlug)) return null
  return day.staffId
}

function rowOr404(id: string): AppointmentRow {
  const row = db.appointments().find((a) => a.id === id)
  if (!row) throw new ApiError('Deze afspraak bestaat niet meer.', 404)
  return row
}

function assertOpen(row: AppointmentRow) {
  if (row.outcome) throw new ApiError('Deze afspraak is al afgerond of geannuleerd.', 409, undefined, 'appointment_closed')
}

function view(row: AppointmentRow | null): AgendaAppointment {
  const a = row && toAgenda(row)
  if (!a) throw new ApiError('Deze afspraak bestaat niet meer.', 404)
  return a
}

/**
 * Afronden: de rij krijgt `outcome`, en bij een klant wordt het een bezoek (met het advies
 * voor thuis) dat meetelt in de kuur. Gedeeld met "Afronden" in het klantdossier.
 */
export function finishAppointment(row: AppointmentRow, products: AppointmentProducts, treatmentNote: string) {
  db.changeAppointment(row.id, (r) => ({ ...r, outcome: 'afgerond', products, ...(treatmentNote.trim() ? { treatmentNote: treatmentNote.trim() } : {}) }))
  if (!row.customerId) return null
  return db.changeAccount(row.customerId, (a) => {
    const counts = a.course && a.course.treatmentSlug === row.treatmentSlug && a.course.done < a.course.total
    const course = counts && a.course ? { ...a.course, done: a.course.done + 1 } : a.course
    const visit: Visit = {
      treatmentSlug: row.treatmentSlug,
      date: row.date > today() ? today() : row.date,
      session: counts && course ? course.done : undefined,
      ...(products.advice.length ? { advice: products.advice } : {}),
      // "Behandeld door" bij elk bezoek (klantdossier, klantkaart, Mijn omgeving).
      staffId: row.staffId ?? DEFAULT_STAFF_ID,
    }
    return [{ ...a, visits: [visit, ...a.visits], course }, { visit, course }]
  })
}

/**
 * Welke sessie van de kuur dit wordt, als de behandeling bij haar lopende kuur hoort.
 * Zelfde regel als de site (`courseStatus`): het laagste vrije nummer, niet "done + aantal geplande + 1".
 * Dat laatste gaf na een annulering midden in de reeks een nummer dat al bezet was.
 */
export function sessionFor(a: Parameters<typeof courseStatus>[0], slug: string): number | undefined {
  const status = courseStatus(a)
  if (!status || status.course.treatmentSlug !== slug || status.finished) return undefined
  return status.nextToBook
}

/**
 * Elke schrijfroute (agenda, klantdossier, Mijn omgeving) rekent het nummer hier zelf;
 * een meegestuurd `session` is hooguit een suggestie (LARAVEL.md §11.1, Kuursessies).
 */
export function nextSession(customerId: string, slug: string): number | undefined {
  const a = db.account(customerId)
  return a ? sessionFor(a, slug) : undefined
}

/** Behandeling, klant óf gast: dezelfde controles voor één afspraak en voor een reeks. */
function assertNewRequest({ customerId, guest, treatmentSlug }: Pick<AgendaNewRequest, 'customerId' | 'guest' | 'treatmentSlug'>) {
  if (!localCatalog.treatments.some((t) => t.slug === treatmentSlug)) throw new ApiError('Kies een behandeling.', 422, { treatment: 'Kies een behandeling.' })
  if (customerId && !db.account(customerId)) throw new ApiError('Deze klant bestaat niet (meer).', 404)
  if (!customerId && (!guest?.name.trim() || !guest.email.trim())) {
    throw new ApiError('Vul een naam en e-mailadres in.', 422, { guest: 'Vul een naam en e-mailadres in.' })
  }
}

/** Een nieuwe, door Sarah ingeplande (dus bevestigde) afspraak. Het sessienummer rekent de server. */
function newRow(requestId: string, req: Omit<AgendaNewRequest, 'requestId' | 'allowOverlap'>): AppointmentRow {
  const { customerId, guest } = req
  return {
    id: customerId ? demoId('af').toLowerCase() : demoId('AF'),
    requestId,
    receivedAt: nowStamp(),
    treatmentSlug: req.treatmentSlug,
    date: req.date,
    time: req.time,
    kind: kindOf(req.treatmentSlug),
    status: 'bevestigd',
    staffId: req.staffId,
    durationMinutes: durationOf(req.treatmentSlug),
    ...(customerId
      ? { customerId, session: nextSession(customerId, req.treatmentSlug) }
      : { guest: { name: guest?.name.trim() ?? '', email: guest?.email.trim() ?? '', phone: guest?.phone?.trim() || undefined, firstVisit: true } }),
  }
}

type AgendaMethods = Pick<
  AdminBackend,
  | 'fetchAgenda'
  | 'addAgendaAppointment'
  | 'addAgendaSeries'
  | 'moveAgendaAppointment'
  | 'confirmAgendaAppointment'
  | 'arriveAgendaAppointment'
  | 'completeAgendaAppointment'
  | 'noShowAgendaAppointment'
  | 'cancelAgendaAppointment'
  | 'addAgendaBlock'
  | 'removeAgendaBlock'
  | 'reportAbsence'
  | 'fetchAgendaFeed'
>

/** Al verwerkte afwezigheidsmeldingen (idempotent op `requestId`, zoals de server). */
const absences = new Map<string, AgendaAbsenceResult>()

export const demoAgendaBackend: AgendaMethods = {
  fetchAgenda: (from, to) =>
    reply(
      {
        staff: [
          ...demoStaff,
          ...staff.filter((s) => !demoStaff.some((d) => d.id === s.id)).map((s) => ({ id: s.id, name: s.name, initials: s.initials })),
        ],
        // Zoals de server (LARAVEL.md, Rechten collega): een collega krijgt alleen haar eigen rijen
        // mee. Het filter in het scherm (`ownOnly`) is dan alleen nog een vangnet.
        appointments: ownOnly(
          allAgenda()
            .filter((a) => a.date >= from && a.date <= to)
            .sort(byStart),
          viewerNow(),
        ),
        blocks: ownOnly(
          db.blocks().filter((b) => b.date >= from && b.date <= to),
          viewerNow(),
        ),
      },
      300,
    ),

  async addAgendaAppointment({ requestId, allowOverlap, customerId, guest, ...req }) {
    const seen = db.appointmentByRequest(requestId)
    if (seen) {
      // Zoals de server: hetzelfde id met andere inhoud is geen herhaling maar een fout (zoals Stripe).
      if (seen.date !== req.date || seen.time !== req.time || seen.treatmentSlug !== req.treatmentSlug || (seen.customerId ?? null) !== (customerId ?? null)) {
        throw new ApiError('Dit verzoek is al verwerkt met andere gegevens. Ververs en probeer opnieuw.', 422, undefined, 'request_mismatch')
      }
      return reply(view(seen), 200)
    }
    assertNewRequest({ customerId, guest, treatmentSlug: req.treatmentSlug })
    // Een collega: alleen een vervolgafspraak op haar eigen dag, voor haar eigen behandelingen, zonder overlap.
    const v = viewerNow()
    if (!v.isOwner) {
      const problem = bookingProblem(v, req, rules())
      if (problem) throw new ApiError(problem, 403, undefined, 'not_your_day')
      req = { ...req, staffId: v.staffId }
    }
    if (allowOverlap && !demoAdminIdentity.access().grants['agenda.override']) {
      throw new ApiError('Over een andere afspraak heen plannen kan alleen Sarah.', 403)
    }
    assertFree({ ...req, durationMinutes: durationOf(req.treatmentSlug) }, undefined, allowOverlap)
    const row = newRow(requestId, { ...req, customerId, guest })
    db.addAppointment(row)
    return reply(view(row), 400)
  },

  /**
   * Zoals de server: elke datum los gecontroleerd. Salon dicht of al voorbij → overgeslagen;
   * bezet → overgeslagen (alleen de eerste mag bewust over een botsing heen). De uitvoerder volgt
   * per datum uit de salondag. Idempotent: elke datum krijgt `requestId:i`.
   */
  async addAgendaSeries({ requestId, allowOverlap, customerId, guest, everyWeeks, count, ...req }) {
    if (!viewerNow().isOwner) throw new ApiError('Een reeks inplannen kan alleen Sarah.', 403, undefined, 'forbidden')
    const problem = seriesProblem(everyWeeks, count)
    if (problem) throw new ApiError(problem, 422, { series: problem })
    assertNewRequest({ customerId, guest, treatmentSlug: req.treatmentSlug })
    const durationMinutes = durationOf(req.treatmentSlug)
    const created: AgendaAppointment[] = []
    const skipped: AgendaSeriesResult['skipped'] = []
    seriesDates(req.date, everyWeeks, count).forEach((date, i) => {
      const key = `${requestId}:${i}`
      const seen = db.appointmentByRequest(key)
      if (seen) return void created.push(view(seen))
      const day = salonDayFor(openingHours, salonExceptions, date)
      // De eerste datum koos Sarah zelf (ook op een dichte dag); de rest moet op een salondag vallen.
      if (i > 0 && !day) return void skipped.push({ date, reason: 'gesloten' })
      const span = { date, time: req.time, durationMinutes, staffId: i === 0 ? req.staffId : (day?.staffId ?? req.staffId) }
      if (hasStarted(span)) return void skipped.push({ date, reason: 'voorbij' })
      try {
        assertFree(span, undefined, i === 0 && allowOverlap)
      } catch {
        return void skipped.push({ date, reason: 'bezet' })
      }
      const row = newRow(key, { ...req, date, staffId: span.staffId, customerId, guest })
      db.addAppointment(row)
      created.push(view(row))
    })
    return reply({ created, skipped }, 500)
  },

  async moveAgendaAppointment(id, { allowOverlap, ...req }) {
    const row = rowOr404(id)
    assertOpen(row)
    assertMine(row)
    const v = viewerNow()
    // Een collega verzet of stelt voor binnen haar eigen dagen; de uitvoerder wijzigen kan alleen Sarah.
    if (!v.isOwner) {
      if (req.staffId && req.staffId !== v.staffId) throw new ApiError('Een andere uitvoerder kiezen kan alleen Sarah.', 403)
      const problem = bookingProblem(v, { date: req.date, treatmentSlug: row.treatmentSlug }, rules())
      if (problem) throw new ApiError(problem, 403, undefined, 'not_your_day')
    }
    if (allowOverlap && !demoAdminIdentity.access().grants['agenda.override']) {
      throw new ApiError('Over een andere afspraak heen plannen kan alleen Sarah.', 403)
    }
    const handledBy = row.status === 'aanvraag' ? { ...demoAdminIdentity.current(), action: 'voorgesteld' as const } : row.handledBy
    const staffId = req.staffId ?? row.staffId ?? DEFAULT_STAFF_ID
    // Andere uitvoerder: zij moet die dag werken en de behandeling doen (zoals de server).
    if (req.staffId && req.staffId !== row.staffId) {
      const problem = performerChoice({ date: req.date, staffId, treatmentSlug: row.treatmentSlug }, openingHours, salonExceptions, staff)
      if (problem) throw new ApiError(`Kan niet: ${staffName(staff, staffId)} ${problem}.`, 422, undefined, 'performer_invalid')
    }
    assertFree({ date: req.date, time: req.time, staffId, durationMinutes: row.durationMinutes ?? durationOf(row.treatmentSlug) }, id, allowOverlap)
    // Verzet door de salon = bevestigd; de klant krijgt een nieuwe uitnodiging.
    return reply(
      view(db.changeAppointment(id, (r) => ({ ...r, date: req.date, time: req.time, staffId, status: 'bevestigd', request: undefined, ...(handledBy ? { handledBy } : {}) }))),
      350,
    )
  },

  async confirmAgendaAppointment(id) {
    const row = rowOr404(id)
    assertOpen(row)
    const problem = requestProblem(viewerNow(), { date: row.date, staffId: row.staffId ?? DEFAULT_STAFF_ID, treatmentSlug: row.treatmentSlug }, rules())
    if (problem) throw new ApiError(problem, 403, undefined, 'not_your_request')
    const handledBy = { ...demoAdminIdentity.current(), action: 'bevestigd' as const }
    return reply(view(db.changeAppointment(id, (r) => ({ ...r, status: 'bevestigd', request: undefined, handledBy }))), 300)
  },

  async arriveAgendaAppointment(id) {
    const row = rowOr404(id)
    assertOpen(row)
    assertMine(row)
    if (row.date !== today()) throw new ApiError('"Binnen" kan alleen op de dag zelf.', 422)
    return reply(view(db.changeAppointment(id, (r) => ({ ...r, arrivedAt: r.arrivedAt ?? nowStamp() }))), 250)
  },

  async completeAgendaAppointment(id, req) {
    const row = rowOr404(id)
    assertOpen(row)
    assertMine(row)
    if (!hasStarted(row)) throw new ApiError('Afronden kan vanaf de begintijd van de afspraak.', 422)
    finishAppointment(row, req.products, req.treatmentNote)
    return reply(view(rowOr404(id)), 400)
  },

  async noShowAgendaAppointment(id) {
    const row = rowOr404(id)
    assertOpen(row)
    assertMine(row)
    if (!hasStarted(row)) throw new ApiError('Niet gekomen kan pas vanaf de begintijd.', 422)
    // No-show-regel (Instellingen › Afspraken & kuren): aan = de kuursessie is op.
    const ruleOn = db.savedPolicy()?.noShowUsesSession ?? policy.noShowUsesSession
    const account = row.customerId ? db.account(row.customerId) : null
    if (account && noShowUsesSession(account.course, row, ruleOn)) {
      db.changeAccount(account.customer.id, (a) => [{ ...a, course: a.course ? { ...a.course, done: a.course.done + 1 } : a.course }, null])
    }
    return reply(view(db.changeAppointment(id, (r) => ({ ...r, outcome: 'no-show' }))), 300)
  },

  async cancelAgendaAppointment(id) {
    const row = rowOr404(id)
    assertOpen(row)
    assertMine(row)
    return reply(view(db.changeAppointment(id, (r) => ({ ...r, outcome: 'geannuleerd', cancelledBy: 'salon' }))), 350)
  },

  async addAgendaBlock({ requestId, ...block }) {
    const id = `blok-${requestId}`
    const seen = db.blocks().find((b) => b.id === id)
    if (seen) return reply(seen, 200)
    if (block.durationMinutes <= 0) throw new ApiError('Een blokkade duurt minstens een kwartier.', 422)
    const created = { id, ...block }
    db.addBlock(created)
    return reply(created, 300)
  },

  // DELETE is idempotent: al weg = ook goed.
  async removeAgendaBlock(id) {
    // Idempotent: al weg = ook goed. Een collega alleen haar eigen blokkade.
    const block = db.blocks().find((b) => b.id === id)
    if (block && !demoAdminIdentity.can('agenda.blocks', block)) throw new ApiError('Deze blokkade is niet van jou.', 403, undefined, 'not_your_block')
    db.removeBlock(id)
    await reply(null, 250)
  },

  async reportAbsence({ requestId, date, staffId, action }) {
    const seen = absences.get(requestId)
    if (seen) return reply(seen, 200)
    if (demoAdminIdentity.access().grants['agenda.roster'] !== 'all') throw new ApiError('Dit deel is alleen voor Sarah.', 403, undefined, 'forbidden')
    const plan = absencePlan(allAgenda(), date, staffId, openingHours, salonExceptions, today())
    if (plan.blocked) throw new ApiError(plan.blocked, 409, undefined, 'not_working')
    if (action === 'naar-eigenaar' && !plan.ownerCanTakeOver) throw new ApiError('Kies afzeggen: er is niemand anders om over te nemen.', 422)
    const day = salonDayFor(openingHours, salonExceptions, date)
    if (!day) throw new ApiError('De salon is deze dag al dicht.', 409, undefined, 'not_working')
    // De afwijkende dag vastleggen (in de demo: de gedeelde lijst, zoals na Opslaan in Instellingen).
    const exception = absenceException(day, staffId, action, staff, `afw-${requestId}`)
    const next = withException(salonExceptions, exception)
    salonExceptions.splice(0, salonExceptions.length, ...next)
    const moved: AgendaAppointment[] = []
    const cancelled: AgendaAppointment[] = []
    // Overnemen mag Sarah's eigen afspraken niet overlappen: dan liever eerlijk weigeren dan dubbel boeken.
    if (action === 'naar-eigenaar') {
      const clash = plan.affected.find((a) => conflictsFor({ ...a, staffId: DEFAULT_STAFF_ID }, allAgenda(), db.blocks(), { ignoreId: a.id }).length > 0)
      if (clash) {
        throw new ApiError(`Om ${clash.time} heb je zelf al een afspraak. Zeg die afspraak af of verzet hem eerst.`, 409, undefined, 'owner_busy')
      }
    }
    for (const a of plan.affected) {
      // Naar Sarah: zelfde tijd, de klant krijgt een nieuwe uitnodiging. Afzeggen: mail met verzetlink.
      const row =
        action === 'naar-eigenaar'
          ? db.changeAppointment(a.id, (r) => ({ ...r, staffId: DEFAULT_STAFF_ID }))
          : db.changeAppointment(a.id, (r) => ({ ...r, outcome: 'geannuleerd', cancelledBy: 'salon' }))
      const v = view(row)
      if (action === 'naar-eigenaar') moved.push(v)
      else cancelled.push(v)
    }
    const result: AgendaAbsenceResult = { exception, moved, cancelled }
    absences.set(requestId, result)
    return reply(result, 450)
  },

  fetchAgendaFeed: () => reply({ url: 'webcal://beautybewust.nl/agenda/feed/demo-voorbeeld.ics' }, 200),
}

/**
 * Vrije tijden voor de webshop: alleen salondagen waarop iemand werkt die de behandeling doet.
 * `ignore` alleen als die afspraak van de ingelogde klant is; `staff` = alleen haar dagen.
 */
export function demoAvailability(treatmentSlug: string, filter: AvailabilityFilter = {}): Promise<AvailabilityDay[]> {
  const { ignore, staff: only } = filter
  const own = ignore && db.appointments().some((a) => a.id === ignore && a.customerId && a.customerId === db.sessionId())
  return reply(
    availability(openingHours, 6, durationOf(treatmentSlug), allAgenda(), db.blocks(), {
      ignoreId: own ? ignore : undefined,
      exceptions: salonExceptions,
      canDo: (id) => canPerform(staff, id, treatmentSlug),
      ...(only ? { staffId: only } : {}),
    }),
    200,
  )
}
